import { ObjectId } from '@freightview/object-id'
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useRef,
} from 'react'
import { Navigate, useLocation, useParams } from 'react-router'
import { create, useStore } from 'zustand'

import { setPathValue, setPathValues, submitForm } from '@fv/client-core'
import type { FullShipment, QuoteStopType } from '@fv/client-types'
import type { Workflow } from '@fv/models'

import InfoBox from '@/components/InfoBox'
import { useLoad } from '@/hooks/shipments'
import { routes } from '@/routes'
import type { ApiQuoteRequestDTO } from '@/types'
import { sanitizeLocation } from '@/utils/locationFuncs'
import { buildQuoteRequestShares } from '@/utils/shares'

import { requireRateLocation } from '../book/bookFuncs'
import {
  defaultLoadHandlingUnit,
  useLoadHandlingUnitFormModels,
} from '../commodities/load-items/loadItemHelpers'
import type { LoadHandlingUnitFormModel } from '../commodities/load-items/types'
import {
  applyTemplateToStops,
  clearTemplateFromStops,
  loadTemplateToQuoteRequestFields,
} from '../load-templates/utils'
import { buildQuoteEquipmentFromHandlingUnits } from '../quote-request/quoteRequestUtils'
import { templateItemsToFormModel } from '../settings/shipment-templates/utils'
import {
  defaultDestination,
  defaultOrigin,
  toApiLocations,
} from '../shipment-location-form/types'
import { type QuoteRequestMode, quoteRequestMapper } from './quoteFuncs'
import type { QuotePageActions, QuotePageState } from './types'

const getInitialState = (workflow: Workflow): QuotePageState => ({
  load: {
    isFreightCollect: false,
    loadId: new ObjectId().toHexString(),
    protectFromFreezing: false,
    singleShipment: false,
    quoteRequestId: '',
    workflow,
    rateLocationType: 'origin',
  },
  locations: [defaultOrigin(), defaultDestination()],
  routeFormRef: null,
  selectedTemplate: null,
  items: [defaultLoadHandlingUnit(workflow)],
  itemsFormKey: 0,
})

function createQuotePageStore(props: {
  load?: FullShipment
  mode?: QuoteRequestMode
  workflow: Workflow
  items?: LoadHandlingUnitFormModel[]
  createFreightCollect?: boolean
}) {
  const { load, mode, workflow, items } = props
  const initializeFromLoad = !!load && !!mode
  const initialState = getInitialState(workflow)

  const clearTemplateState = (
    prev: QuotePageState,
  ): Partial<QuotePageState> => {
    const template = prev.selectedTemplate
    if (!template) return {}

    return {
      selectedTemplate: null,
      locations: clearTemplateFromStops(prev.locations, template, (_, type) =>
        type === 'origin' ? defaultOrigin() : defaultDestination(),
      ),
      ...(template.items?.length && {
        items: [defaultLoadHandlingUnit(workflow)],
        itemsFormKey: prev.itemsFormKey + 1,
      }),
    }
  }

  return create<QuotePageActions & QuotePageState>((...store) => {
    const [set, get] = store
    return {
      ...initialState,
      ...(items?.length && { items }),
      load: {
        ...initialState.load,
        workflow,
        schedulePickup: workflow !== 'parcel' || !!load?.schedulePickup,
        ...(props.createFreightCollect && {
          isFreightCollect: true,
        }),
      },
      ...(initializeFromLoad &&
        quoteRequestMapper.ltlRequest({
          load,
          mode,
        })),
      actions: {
        applyLoadTemplate: template => {
          set(current => {
            const prev = { ...current, ...clearTemplateState(current) }
            return {
              selectedTemplate: template,
              locations: applyTemplateToStops(prev.locations, template),
              ...(template.items?.length
                ? {
                    items: templateItemsToFormModel(template.items, workflow),
                    itemsFormKey: prev.itemsFormKey + 1,
                  }
                : { items: prev.items, itemsFormKey: prev.itemsFormKey }),
            }
          })
        },
        clearLoadTemplate: () => {
          set(clearTemplateState)
        },
        buildRequest: () => {
          const {
            actions,
            load,
            locations,
            deliveryOption,
            selectedTemplate,
            items,
          } = get()
          const isValidRoute = actions.validateForm()
          if (!isValidRoute) return

          return {
            ...(requireRateLocation(locations, load.isFreightCollect) && {
              rateLocationType: load.rateLocationType,
            }),
            isLiveLoad: true,
            locations: toApiLocations(locations),
            orderNumber: '',
            workflow: load.workflow,
            schedulePickup: load.schedulePickup,
            ...loadTemplateToQuoteRequestFields(selectedTemplate),
            ...buildQuoteRequestShares(locations),
            equipment: [
              buildQuoteEquipmentFromHandlingUnits({
                deliveryOption,
                items,
                load,
              }),
            ],
          } satisfies ApiQuoteRequestDTO
        },
        handleLoadChange: e => {
          set(prev => {
            const { name } = e.target
            const load = { ...prev.load }

            if (name === 'rateLocationType') {
              load[name] = e.target.value as QuoteStopType
            }

            if (
              name === 'isFreightCollect' ||
              name === 'protectFromFreezing' ||
              name === 'singleShipment'
            ) {
              const checkbox = e.target as HTMLInputElement
              load[name] = checkbox.checked
            }

            if (name === 'schedulePickup') {
              const radio = e.target as HTMLInputElement
              load.schedulePickup = radio.value === 'true'
            }

            if (
              name === 'isFreightCollect' &&
              load.workflow === 'parcel' &&
              load.isFreightCollect
            ) {
              load.schedulePickup = false
            }

            return { load }
          })
        },
        setLocationData: (stopType, data) => {
          set(prev => {
            const { stopDate, ...location } = data
            const stopIx = stopType === 'origin' ? 0 : 1
            let nextStop = { ...prev.locations[stopIx] }
            setPathValues(nextStop, location)

            nextStop = sanitizeLocation(nextStop, workflow)

            if (stopDate || stopDate === null) {
              setPathValue(nextStop, 'stopDate', stopDate)
            }

            const load = { ...prev.load }
            const locations = prev.locations.map((x, ix) =>
              ix === stopIx ? nextStop : x,
            )

            return { load, locations }
          })
        },
        setFormRef: ref => {
          set({ routeFormRef: ref })
        },
        setItems: items => {
          set({ items })
        },
        updateDeliveryOption(deliveryOption) {
          set(() => ({
            deliveryOption,
          }))
        },
        validateForm: () => {
          const formEl = get().routeFormRef
          const isValid = formEl?.checkValidity() ?? false

          if (!isValid) submitForm(formEl)
          return isValid
        },
      },
    }
  })
}

type QuotePageStore = ReturnType<typeof createQuotePageStore>
type Params = { loadId?: string; mode?: QuoteRequestMode }
type Props = PropsWithChildren<{ workflow: 'ltl' | 'parcel' }>
const QuotePageContext = createContext<QuotePageStore | null>(null)

export const QuotePageProvider = ({ children, workflow }: Props) => {
  const { loadId, mode } = useParams<Params>()
  const { search } = useLocation()
  const loadQuery = useLoad(loadId, { showDiscardedError: true })
  const handlingUnitsQuery = useLoadHandlingUnitFormModels(loadQuery.data)
  const storeRef = useRef<QuotePageStore>(null)

  if (
    loadQuery.isError ||
    (loadQuery.isLoading && loadQuery.isFetching) ||
    handlingUnitsQuery.isLoading
  ) {
    return (
      <div className="py-10 px-6 overflow-auto bg-white b1200:pb-28">
        <InfoBox icon={loadQuery.isError ? 'exclamation-triangle' : 'sync'}>
          {loadQuery.isError ? 'Shipment not found.' : 'Loading quote...'}
        </InfoBox>

        {loadQuery.isError && <Navigate to={routes.quote(workflow)} replace />}
      </div>
    )
  }

  if (!storeRef.current) {
    const queryString = new URLSearchParams(search)
    storeRef.current = createQuotePageStore({
      load: loadQuery.data,
      workflow,
      mode,
      items: handlingUnitsQuery.data,
      createFreightCollect: !!queryString.get('cr'),
    })
  }

  return (
    <QuotePageContext.Provider value={storeRef.current}>
      {children}
    </QuotePageContext.Provider>
  )
}
export function useQuotePageCtx<T>(
  selector: (state: QuotePageActions & QuotePageState) => T,
): T {
  const store = useContext(QuotePageContext)

  if (!store) {
    throw new Error('Missing QuotePageContext.Provider in the tree')
  }
  return useStore(store, selector)
}

export const useQuotePageWorkflow = () =>
  useQuotePageCtx(ctx => ctx.load.workflow)
